import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { useAuth } from '../context/AuthContext';
import DeviceSelector, { formatDeviceLabel, buildUserDeviceIndex } from '../components/DeviceSelector';
import { useFirestoreEnrichment } from '../hooks/useFirestoreEnrichment';
import ConfirmDialog from '../components/ConfirmDialog';
import { Input } from '../components/pilot-ui/input';
import { Label } from '../components/pilot-ui/label';
import { Checkbox } from '../components/pilot-ui/checkbox';
import { Textarea } from '../components/pilot-ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/pilot-ui/select';
import { Alert, AlertListResponse, Device } from '../types';
import { formatTimestamp } from '../utils/formatTimestamp';
import Button from '../components/ui/Button';

const SEV_COLORS: Record<string, string> = {
  urgent: 'bg-status-error text-status-error-foreground border-status-error-border',
  warning: 'bg-status-warning text-status-warning-foreground border-status-warning-border',
  info: 'bg-status-info text-status-info-foreground border-status-info-border',
};

export type AlertAudience = 'all' | 'admin' | 'user';

// Resolves run a few at a time so a large selection neither serialises into a
// long wait nor floods the API.
const RESOLVE_CONCURRENCY = 4;

export const ALERTS_TITLES = {
  status: 'Show open alerts, closed alerts, or both.',
  severity: 'Show only alerts of one severity.',
  type: 'Show only alerts of this exact type, e.g. filter_needs_replacement.',
  audience: 'Who the alert went to. "Alerts to Admins only" were kept on this web console and never reached the user\'s app; "Alerts to Users only" were sent to the user\'s app.',
  refresh: 'Reload the alert list with the current filters.',
  trigger: 'Create a synthetic alert on a device to test the full delivery pipeline.',
  selectAllHeader: 'Tick to select every open alert in the list below; untick to clear the selection.',
  selectAll: 'Select every open alert in the list below (closed alerts cannot be resolved).',
  deselectAll: 'Clear the selection.',
  resolveSelected: 'Close every selected alert with reason user_resolved. User alerts also disappear from the user\'s app. An alert whose condition is still present fires again on the next detection cycle.',
  selectRow: 'Select this alert for Resolve All Selected.',
  resolveRow: 'Close this one alert with reason user_resolved.',
} as const;

const AlertsPage: React.FC = () => {
  const { permissions } = useAuth();
  const canManage = permissions?.includes('manage_alerts') ?? false;

  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'active' | 'closed' | 'all'>('active');
  const [severityFilter, setSeverityFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [audience, setAudience] = useState<AlertAudience>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkResult, setBulkResult] = useState<{ ok: number; failed: string[] } | null>(null);

  const [showTestModal, setShowTestModal] = useState(false);
  const [resolveTarget, setResolveTarget] = useState<Alert | null>(null);
  const refreshButtonRef = useRef<HTMLButtonElement>(null);

  // FS-891: the owner names beside each Clip need read_devices, which
  // read_alerts does not include; without it the page says so and works on.
  const { enrichmentMap, unavailableReason: ownerNamesReason } = useFirestoreEnrichment();
  const [devices, setDevices] = useState<Device[]>([]);

  useEffect(() => {
    axiosInstance.get<Device[]>(`${config.devicesApiUrl}/devices`)
      .then(r => setDevices(r.data))
      .catch(() => { /* table still renders raw device_name on failure */ });
  }, []);

  const deviceLabelByName = useMemo(() => {
    const userDeviceIndex = buildUserDeviceIndex(enrichmentMap);
    const map: Record<string, string> = {};
    devices.forEach(d => {
      map[d.name] = formatDeviceLabel(d, enrichmentMap[d.name], userDeviceIndex);
    });
    return map;
  }, [devices, enrichmentMap]);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = { status: statusFilter, limit: '100' };
      if (severityFilter) params.severity = severityFilter;
      if (typeFilter) params.type = typeFilter;
      if (audience !== 'all') params.audience = audience;
      const resp = await axiosInstance.get<AlertListResponse>(`${config.alertsApiUrl}`, { params });
      setAlerts(resp.data.rows);
      setTotal(resp.data.total);
      // Keep only selections that are still in the list and still open.
      const openIds = new Set(resp.data.rows.filter(a => !a.closed_at).map(a => a.id));
      setSelected(prev => new Set(Array.from(prev).filter(id => openIds.has(id))));
    } catch (err) {
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, severityFilter, typeFilter, audience]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const selectableIds = useMemo(
    () => (canManage ? alerts.filter(a => !a.closed_at).map(a => a.id) : []),
    [alerts, canManage],
  );
  const allSelected = selectableIds.length > 0 && selectableIds.every(id => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleOne = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(selectableIds));
  const deselectAll = () => setSelected(new Set());

  const resolveSelected = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkResult(null);
    setBulkProgress({ done: 0, total: ids.length });
    const failed: string[] = [];
    let ok = 0;
    let next = 0;
    const worker = async () => {
      while (next < ids.length) {
        const id = ids[next++];
        try {
          await axiosInstance.post(`${config.alertsApiUrl}/${id}/resolve`, { reason: 'user_resolved' });
          ok++;
        } catch (err: any) {
          const a = alerts.find(x => x.id === id);
          const label = a ? `${a.title} on ${deviceLabelByName[a.device_name] || a.device_name}` : id;
          failed.push(`${label}: ${err?.response?.data?.message ?? err?.message ?? 'failed'}`);
        }
        setBulkProgress(p => (p ? { ...p, done: p.done + 1 } : p));
      }
    };
    await Promise.all(Array.from({ length: Math.min(RESOLVE_CONCURRENCY, ids.length) }, worker));
    setBulkProgress(null);
    setBulkResult({ ok, failed });
    await fetchAlerts();
  };
  const resolving = bulkProgress !== null;

  // Resolve confirmation flows through ResolveAlertModal — replaces the
  // browser-native window.confirm to match the dialog style used elsewhere
  // in the app (e.g. UsersPage delete).

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Alerts</h1>
        {canManage && (
          <Button
            variant="primary"
            onClick={() => setShowTestModal(true)}
            title={ALERTS_TITLES.trigger}
          >
            Trigger Test Alert
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <div><Label htmlFor="alerts-status">Status</Label><p id="alerts-status-help" className="text-xs text-muted-foreground">{ALERTS_TITLES.status}</p>
          <Select value={statusFilter} onValueChange={value => { if (value === 'active' || value === 'closed' || value === 'all') setStatusFilter(value); }} items={[{ value: 'active', label: 'Active' }, { value: 'closed', label: 'Closed' }, { value: 'all', label: 'All' }]}>
            <SelectTrigger id="alerts-status" title={ALERTS_TITLES.status} aria-describedby="alerts-status-help"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="closed">Closed</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label htmlFor="alerts-severity">Severity</Label><p id="alerts-severity-help" className="text-xs text-muted-foreground">{ALERTS_TITLES.severity}</p>
          <Select value={severityFilter} onValueChange={value => setSeverityFilter(value ?? '')} items={[{ value: '', label: 'Any severity' }, { value: 'urgent', label: 'Urgent' }, { value: 'warning', label: 'Warning' }, { value: 'info', label: 'Info' }]}>
            <SelectTrigger id="alerts-severity" title={ALERTS_TITLES.severity} aria-describedby="alerts-severity-help"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="">Any severity</SelectItem><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="warning">Warning</SelectItem><SelectItem value="info">Info</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="grow"><Label htmlFor="alerts-type">Type</Label><p id="alerts-type-help" className="text-xs text-muted-foreground">{ALERTS_TITLES.type}</p>
          <Input id="alerts-type" type="text" placeholder="Type filter (e.g. filter_needs_replacement)" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} title={ALERTS_TITLES.type} aria-describedby="alerts-type-help" />
        </div>
        <div><Label htmlFor="alerts-audience">Audience</Label><p id="alerts-audience-help" className="text-xs text-muted-foreground">{ALERTS_TITLES.audience}</p>
          <Select value={audience} onValueChange={value => { if (value === 'all' || value === 'admin' || value === 'user') setAudience(value); }} items={[{ value: 'all', label: 'All alerts' }, { value: 'admin', label: 'Alerts to Admins only' }, { value: 'user', label: 'Alerts to Users only' }]}>
            <SelectTrigger id="alerts-audience" title={ALERTS_TITLES.audience} aria-describedby="alerts-audience-help"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All alerts</SelectItem><SelectItem value="admin">Alerts to Admins only</SelectItem><SelectItem value="user">Alerts to Users only</SelectItem></SelectContent>
          </Select>
        </div>
        <Button ref={refreshButtonRef} variant="secondary" onClick={fetchAlerts} title={ALERTS_TITLES.refresh}>Refresh</Button>
      </div>

      {canManage && (
        <div className="flex flex-wrap gap-3 mb-4 items-center">
          <Button variant="secondary" onClick={selectAll} disabled={resolving || selectableIds.length === 0}
            title={ALERTS_TITLES.selectAll}>
            Select all
          </Button>
          <Button variant="secondary" onClick={deselectAll} disabled={resolving || selected.size === 0}
            title={ALERTS_TITLES.deselectAll}>
            Deselect all
          </Button>
          <Button variant="primary" onClick={resolveSelected} disabled={resolving || selected.size === 0}
            title={ALERTS_TITLES.resolveSelected}>
            {resolving
              ? `Resolving ${bulkProgress!.done} of ${bulkProgress!.total}…`
              : `Resolve All Selected (${selected.size})`}
          </Button>
        </div>
      )}
      {bulkResult && (
        <div role="alert"
          className={`mb-4 p-3 rounded-md text-sm ${bulkResult.failed.length ? 'border border-status-error-border bg-status-error text-status-error-foreground' : 'border border-status-success-border bg-status-success text-status-success-foreground'}`}>
          Resolved {bulkResult.ok} alert{bulkResult.ok !== 1 ? 's' : ''}.
          {bulkResult.failed.length > 0 && (
            <>
              {' '}{bulkResult.failed.length} failed and stay selected:
              <ul className="list-disc ml-5 mt-1">
                {bulkResult.failed.map(f => <li key={f}>{f}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      {ownerNamesReason && <p className="text-sm text-muted-foreground mb-2">{ownerNamesReason}</p>}
      {loading && <p className="text-muted-foreground">Loading...</p>}
      {error && <p className="text-status-error-foreground">{error}</p>}

      {!loading && !error && (() => {
        const visible = alerts;
        return (
        <>
          <p className="text-sm text-gray-600 mb-2">
            {visible.length} of {total} alert{total !== 1 ? 's' : ''}
            {visible.length < total && ' — only the alerts shown here can be selected'}
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-card border border-border">
              <thead className="bg-muted">
                <tr>
                  {canManage && (
                    <th className="py-2 px-3 border-b w-8">
                      <Label className="inline-flex items-center gap-1 text-xs">
                        <Checkbox
                          aria-label="Select all alerts"
                          title={ALERTS_TITLES.selectAllHeader}
                          checked={allSelected}
                          indeterminate={someSelected}
                          onCheckedChange={() => (allSelected ? deselectAll() : selectAll())}
                          disabled={resolving || selectableIds.length === 0}
                        />
                        All
                      </Label>
                    </th>
                  )}
                  <th className="text-left py-2 px-3 border-b">Severity</th>
                  <th className="text-left py-2 px-3 border-b">Device</th>
                  <th className="text-left py-2 px-3 border-b">Type</th>
                  <th className="text-left py-2 px-3 border-b">Title</th>
                  <th className="text-left py-2 px-3 border-b">Opened</th>
                  <th className="text-left py-2 px-3 border-b">Closed</th>
                  <th className="text-left py-2 px-3 border-b">FCM</th>
                  <th className="text-left py-2 px-3 border-b">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(a => (
                  <tr key={a.id} className={`hover:bg-gray-50 border-b ${selected.has(a.id) ? 'bg-blue-50' : ''}`}>
                    {canManage && (
                      <td className="py-2 px-3">
                        {!a.closed_at && (
                          <Label className="inline-flex items-center gap-1 text-xs">
                            <Checkbox
                              aria-label={`Select ${a.title} on ${deviceLabelByName[a.device_name] || a.device_name}`}
                              title={ALERTS_TITLES.selectRow}
                              checked={selected.has(a.id)}
                              onCheckedChange={() => toggleOne(a.id)}
                              disabled={resolving}
                            />
                            Select
                          </Label>
                        )}
                      </td>
                    )}
                    <td className="py-2 px-3">
                      <div className="flex flex-col gap-1">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-sm border ${SEV_COLORS[a.severity] || ''}`}>
                          {a.severity}
                        </span>
                        {a.internal_only && (
                          <span className="text-xs px-2 py-0.5 rounded-sm border border-status-neutral-border bg-status-neutral text-status-neutral-foreground" title="Admin-only — not shown in the mobile app">
                            Admin only
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 font-mono text-sm">
                      {deviceLabelByName[a.device_name] || a.device_name}
                    </td>
                    <td className="py-2 px-3 text-sm">{a.type}</td>
                    <td className="py-2 px-3">{a.title}</td>
                    <td className="py-2 px-3 text-sm text-gray-600">{formatTimestamp(a.opened_at)}</td>
                    <td className="py-2 px-3 text-sm text-gray-600">
                      {a.closed_at ? `${formatTimestamp(a.closed_at)} (${a.close_reason})` : '—'}
                    </td>
                    <td className="py-2 px-3 text-xs">{a.fcm_sent_at ? '✓' : '—'}</td>
                    <td className="py-2 px-3">
                      {!a.closed_at && canManage && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setResolveTarget(a)}
                          title={ALERTS_TITLES.resolveRow}
                        >Resolve</Button>
                      )}
                    </td>
                  </tr>
                ))}
                {visible.length === 0 && (
                  <tr><td colSpan={canManage ? 9 : 8} className="py-6 px-3 text-center text-muted-foreground">No alerts match filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
        );
      })()}

      {showTestModal && (
        <TestAlertModal
          devices={devices}
          enrichmentMap={enrichmentMap}
          onClose={() => setShowTestModal(false)}
          onSent={fetchAlerts}
        />
      )}
      {resolveTarget && (
        <ResolveAlertModal
          alert={resolveTarget}
          deviceLabel={deviceLabelByName[resolveTarget.device_name] || resolveTarget.device_name}
          onClose={() => setResolveTarget(null)}
          onResolved={() => { setResolveTarget(null); fetchAlerts(); }}
          returnFocusTo={refreshButtonRef}
        />
      )}
    </div>
  );
};

interface TestAlertModalProps {
  devices: Device[];
  enrichmentMap: import('../types').DeviceEnrichmentMap;
  onClose: () => void;
  onSent: () => void;
}

const TestAlertModal: React.FC<TestAlertModalProps> = ({ devices, enrichmentMap, onClose, onSent }) => {
  const [deviceName, setDeviceName] = useState('');
  const [type, setType] = useState('filter_needs_replacement');
  const [severity, setSeverity] = useState<'info' | 'warning' | 'urgent'>('warning');
  const [title, setTitle] = useState('Test alert');
  const [body, setBody] = useState('Smoke-test from /alerts admin page');
  const [internalOnly, setInternalOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [missingDevice, setMissingDevice] = useState(false);

  const submit = async () => {
    const device = devices.find(d => d.name === deviceName);
    if (!device) {
      setMissingDevice(true);
      return;
    }
    setMissingDevice(false);
    setSubmitting(true);
    setResult(null);
    try {
      const r = await axiosInstance.post<{ id: string; status: string }>(`${config.alertsApiUrl}/test`, {
        device_id: device.id,
        type, severity, title, body,
        internal_only: internalOnly,
        context: { source: 'admin-test' },
      });
      setResult(`Enqueued: ${r.data.id}`);
      onSent();
    } catch (e: any) {
      setResult(`Failed: ${e?.response?.data?.message || 'unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog open title="Trigger Test Alert" confirmLabel="Send" confirmTitle="Create this test alert on the chosen device now" submitting={submitting} error={result?.startsWith('Failed') ? result : null} onConfirm={submit} onCancel={onClose}>
        <div className="space-y-3">
          <div>
            <Label htmlFor="test-alert-device">Device</Label>
            <p id="test-alert-device-help" className="text-xs text-muted-foreground">Choose the device that receives this test alert.</p>
            <DeviceSelector
              devices={devices}
              enrichmentMap={enrichmentMap}
              value={deviceName}
              onChange={e => {
                setDeviceName(e.target.value);
                setMissingDevice(false);
              }}
              valueMode="name"
              className="w-full"
              name="test-alert-device"
              describedBy={`test-alert-device-help${missingDevice ? ' test-alert-device-error' : ''}`}
              invalid={missingDevice}
            />
            {missingDevice && <p id="test-alert-device-error" role="alert">Choose a device before sending.</p>}
          </div>
          <div>
            <Label htmlFor="test-alert-type">Type</Label><p id="test-alert-type-help" className="text-xs text-muted-foreground">The alert type sent through the delivery path.</p>
            <Input id="test-alert-type" type="text" value={type} onChange={e => setType(e.target.value)} aria-describedby="test-alert-type-help" />
          </div>
          <div>
            <Label htmlFor="test-alert-severity">Severity</Label><p id="test-alert-severity-help" className="text-xs text-muted-foreground">How urgently this alert appears to its audience.</p>
            <Select value={severity} onValueChange={value => { if (value === 'info' || value === 'warning' || value === 'urgent') setSeverity(value); }} items={[{ value: 'info', label: 'info' }, { value: 'warning', label: 'warning' }, { value: 'urgent', label: 'urgent' }]}>
              <SelectTrigger id="test-alert-severity" aria-describedby="test-alert-severity-help"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="info">info</SelectItem><SelectItem value="warning">warning</SelectItem><SelectItem value="urgent">urgent</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="test-alert-title">Title</Label><p id="test-alert-title-help" className="text-xs text-muted-foreground">Headline shown with the test alert.</p>
            <Input id="test-alert-title" type="text" value={title} onChange={e => setTitle(e.target.value)} aria-describedby="test-alert-title-help" />
          </div>
          <div>
            <Label htmlFor="test-alert-body">Body</Label><p id="test-alert-body-help" className="text-xs text-muted-foreground">Message delivered with the test alert.</p>
            <Textarea id="test-alert-body" value={body} onChange={e => setBody(e.target.value)} rows={3} aria-describedby="test-alert-body-help" />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="test-alert-internal" checked={internalOnly} onCheckedChange={checked => setInternalOnly(checked === true)} aria-describedby="test-alert-internal-help" />
            <Label htmlFor="test-alert-internal">Admin only (no Firestore mirror, no FCM push)</Label>
          </div>
          <p id="test-alert-internal-help" className="text-xs text-muted-foreground">Keep this alert in the web console and do not send a push to the user's app.</p>
          {result && !result.startsWith('Failed') && <div role="status" className="rounded-sm border border-status-success-border bg-status-success p-2 text-sm text-status-success-foreground">{result}</div>}
        </div>
    </ConfirmDialog>
  );
};

// ---------------------------------------------------------------------------
// Resolve confirmation modal — same shape as UsersPage DeleteUserModal so the
// dialog styling matches admin actions elsewhere.
// ---------------------------------------------------------------------------

interface ResolveAlertModalProps {
  alert: Alert;
  deviceLabel: string;
  onClose: () => void;
  onResolved: () => void;
  returnFocusTo: React.RefObject<HTMLButtonElement | null>;
}

const ResolveAlertModal: React.FC<ResolveAlertModalProps> = ({ alert, deviceLabel, onClose, onResolved, returnFocusTo }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResolve = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await axiosInstance.post(`${config.alertsApiUrl}/${alert.id}/resolve`, { reason: 'user_resolved' });
      onResolved();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Failed to resolve');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog open title="Resolve Alert" tone="danger" confirmLabel="Resolve" confirmTitle="Close this alert with reason user_resolved" submitting={submitting} error={error} onConfirm={handleResolve} onCancel={onClose} returnFocusTo={returnFocusTo} message={
      <>
          <p className="text-gray-700 mb-3">
            Resolve <strong>{alert.title}</strong> on <span className="font-mono text-sm">{deviceLabel}</span>?
          </p>
          <p className="text-sm text-muted-foreground mb-4">
            Closes the alert with reason <code>user_resolved</code>. The user's app removes it from
            their active list. If the underlying condition is still present, a new alert will fire on
            the next detection cycle.
          </p>
      </>
    } />
  );
};

export default AlertsPage;
